import { AlertCircle, Check, Loader2, Lock, Plug, X } from "lucide-react";
import { useState } from "react";

import { hostsApi, apiErrorMessage, type ServerHost } from "../lib/api";
import { Modal } from "./Modal";

interface AddHostModalProps {
  onClose: () => void;
  onAdd: (host: ServerHost) => void;
}

type TestState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ok" }
  | { kind: "error"; message: string; code?: string };

const LABEL =
  "flex items-center justify-between text-[11.5px] font-medium text-text-dim";
const HINT = "font-normal text-text-faint";
const FIELD_INPUT =
  "w-full bg-bg-2 border border-border rounded-md px-2.5 py-2 text-[12.5px] text-text font-sans outline-none transition-[border-color,box-shadow] duration-100 focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]";
const SEG_BTN_BASE = "px-2.5 py-1.25 text-[11.5px] rounded-md text-text-muted";
const SEG_BTN_ACTIVE =
  "bg-bg-1 text-text shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_1px_2px_rgba(0,0,0,0.2)] in-data-[theme=light]:shadow-[0_0_0_1px_var(--border-strong)]";

export function AddHostModal({ onClose, onAdd }: AddHostModalProps) {
  const [label, setLabel] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("22");
  const [sshUser, setSshUser] = useState("");
  const [auth, setAuth] = useState<"key" | "password" | "agent">("password");
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");

  const [test, setTest] = useState<TestState>({ kind: "idle" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const valid = Boolean(label.trim() && host.trim() && sshUser.trim());
  const portNum = Number(port) || 22;

  const buildCredentials = () => {
    if (auth === "key") return { privateKey, passphrase: passphrase || undefined };
    if (auth === "password") return { password };
    return {};
  };

  const handleTest = async () => {
    if (!valid) {
      setFormError("Label, host, and user are required.");
      return;
    }
    setFormError(null);
    setTest({ kind: "running" });
    try {
      const { data } = await hostsApi.test({
        hostname: host.trim(),
        port: portNum,
        sshUser: sshUser.trim(),
        authMethod: auth,
        ...buildCredentials(),
      });
      setTest(data.ok ? { kind: "ok" } : { kind: "error", message: data.error ?? "test failed", code: data.code });
    } catch (err) {
      const { message, code } = apiErrorMessage(err);
      setTest({ kind: "error", message, code });
    }
  };

  const handleSave = async () => {
    if (!valid) {
      setFormError("Label, host, and user are required.");
      return;
    }
    setFormError(null);
    setSaving(true);
    setTest({ kind: "running" });
    try {
      const { data } = await hostsApi.create({
        name: label.trim(),
        hostname: host.trim(),
        port: portNum,
        sshUser: sshUser.trim(),
        authMethod: auth,
        tags: tags.length ? tags : undefined,
        ...buildCredentials(),
      });
      setTest({ kind: "ok" });
      onAdd(data.host);
      onClose();
    } catch (err) {
      const { message, code } = apiErrorMessage(err);
      setTest({ kind: "error", message, code });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add a new host"
      sub="Connect over SSH. Credentials are encrypted at rest."
      onClose={onClose}
      foot={
        <>
          <button
            className="px-2.5 py-2 rounded-lg border border-border bg-bg-2 text-text-dim text-[12.5px] inline-flex items-center gap-1.5 hover:text-text hover:border-border-strong hover:bg-bg-3 transition-colors duration-100 disabled:opacity-60"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="px-2.5 py-2 rounded-lg border border-border bg-bg-2 text-text-dim text-[12.5px] inline-flex items-center gap-1.5 hover:text-text hover:border-border-strong hover:bg-bg-3 transition-colors duration-100 disabled:opacity-60"
            onClick={handleTest}
            disabled={saving || test.kind === "running"}
          >
            {test.kind === "running" ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />}
            Test
          </button>
          <button
            className="btn-primary inline-flex items-center justify-center gap-1.75 px-3 py-2 rounded-lg text-[12.5px] disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={saving || !valid}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />}
            {saving ? "Saving…" : "Test & save"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-1.5">
        <label className={LABEL}>
          <span>Label</span>
          <span className={HINT}>Display name in your sidebar</span>
        </label>
        <input className={FIELD_INPUT} value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>

      <div className="grid grid-cols-[2fr_1fr] gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className={LABEL}><span>Host</span></label>
          <input
            className={`${FIELD_INPUT} font-mono`}
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="ip / hostname"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className={LABEL}><span>Port</span></label>
          <input className={`${FIELD_INPUT} font-mono`} value={port} onChange={(e) => setPort(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className={LABEL}><span>User</span></label>
          <input
            className={`${FIELD_INPUT} font-mono`}
            value={sshUser}
            onChange={(e) => setSshUser(e.target.value)}
            placeholder="ubuntu"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={LABEL}><span>Authentication</span></label>
        <div className="inline-flex self-start p-0.5 bg-bg-2 border border-border rounded-lg">
          <button className={`${SEG_BTN_BASE} ${auth === "key" ? SEG_BTN_ACTIVE : ""}`} onClick={() => setAuth("key")}>
            SSH key
          </button>
          <button className={`${SEG_BTN_BASE} ${auth === "password" ? SEG_BTN_ACTIVE : ""}`} onClick={() => setAuth("password")}>
            Password
          </button>
        </div>
      </div>

      {auth === "key" && (
        <>
          <div className="flex flex-col gap-1.5">
            <label className={LABEL}>
              <span>Private key</span>
              <span className={HINT}>
                <Lock size={11} style={{ verticalAlign: "-2px" }} /> encrypted at rest
              </span>
            </label>
            <textarea
              className={`${FIELD_INPUT} font-mono resize-none min-h-24`}
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;..."
              spellCheck={false}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={LABEL}>
              <span>Passphrase</span>
              <span className={HINT}>optional</span>
            </label>
            <input
              type="password"
              className={FIELD_INPUT}
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </>
      )}

      {auth === "password" && (
        <div className="flex flex-col gap-1.5">
          <label className={LABEL}>
            <span>Password</span>
            <span className={HINT}>
              <Lock size={11} style={{ verticalAlign: "-2px" }} /> encrypted at rest
            </span>
          </label>
          <input
            type="password"
            className={FIELD_INPUT}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className={LABEL}><span>Tags</span></label>
        <div className="flex flex-wrap gap-1.5 bg-bg-2 border border-border rounded-md px-2 py-1.5">
          {tags.map((t, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 bg-accent-soft text-accent text-[11px] font-medium px-1.75 py-0.5 rounded-xs"
            >
              {t}
              <span
                className="text-accent opacity-60 hover:opacity-100 cursor-pointer"
                onClick={() => setTags(tags.filter((_, j) => j !== i))}
              >
                <X size={9} />
              </span>
            </span>
          ))}
          <input
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tagDraft.trim()) {
                setTags([...tags, tagDraft.trim()]);
                setTagDraft("");
              }
            }}
            placeholder="Add tag…"
            className="flex-1 min-w-20 bg-transparent border-0 outline-none px-1 py-0.5 text-xs text-text"
          />
        </div>
      </div>

      <TestPanel state={test} />
      {formError && (
        <div className="flex items-center gap-2 text-[11.5px] text-danger">
          <AlertCircle size={12} /> {formError}
        </div>
      )}
    </Modal>
  );
}

function TestPanel({ state }: { state: TestState }) {
  if (state.kind === "idle") {
    return (
      <div className="text-[11px] text-text-faint">
        Run “Test” to verify connectivity, or “Test &amp; save” to do both.
      </div>
    );
  }
  if (state.kind === "running") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-text-dim">
        <Loader2 size={12} className="animate-spin" />
        Verifying SSH connection…
      </div>
    );
  }
  if (state.kind === "ok") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-success">
        <span className="grid w-3.5 h-3.5 place-items-center rounded-full bg-success-soft">
          <Check size={9} strokeWidth={2.4} />
        </span>
        SSH handshake succeeded.
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-[12px] text-danger bg-danger-soft border border-danger/30 rounded-md px-2.5 py-2">
      <AlertCircle size={13} className="mt-px" />
      <div className="flex flex-col gap-0.5">
        <div className="font-medium">Connection failed</div>
        <div className="text-[11.5px] opacity-90">{state.message}</div>
        {state.code && (
          <div className="font-mono text-[10.5px] opacity-70">{state.code}</div>
        )}
      </div>
    </div>
  );
}
